import { useCallback, useEffect, useRef } from 'react'

/**
 * Hook to trap focus within a modal or dialog component
 * @param {boolean} isActive - Whether the focus trap should be active
 * @returns {Object} - trapRef to attach to the trap container and
 *   registerAlly to register portaled descendants (e.g. dropdown menus)
 *   that should be treated as part of the trap.
 *   registerAlly(node, anchor?) returns an unregister function. When
 *   `anchor` is a focusable element inside the trap, the ally's focusables
 *   are spliced into the tab order immediately after the anchor, preserving
 *   natural keyboard navigation for portaled dropdowns.
 */
function useFocusTrap(isActive = true) {
  const trapRef = useRef(null)
  const alliesRef = useRef(new Map())
  const previousActiveElement = useRef(null)

  const registerAlly = useCallback((node, anchor = null) => {
    if (!node) return () => {}
    alliesRef.current.set(node, anchor)
    return () => {
      alliesRef.current.delete(node)
    }
  }, [])

  useEffect(() => {
    if (!isActive || !trapRef.current) return undefined

    previousActiveElement.current = document.activeElement

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable]',
    ].join(', ')

    const getContainers = () => [trapRef.current, ...alliesRef.current.keys()].filter(Boolean)

    const isInsideTrap = (el) => getContainers().some((container) => container.contains(el))

    const isVisible = (el) => {
      const style = window.getComputedStyle(el)
      return style.display !== 'none' && style.visibility !== 'hidden'
    }

    const queryFocusables = (container) => Array
      .from(container.querySelectorAll(focusableSelectors))
      .filter(isVisible)

    const getFocusableElements = () => {
      const focusables = trapRef.current ? queryFocusables(trapRef.current) : []

      alliesRef.current.forEach((anchor, allyNode) => {
        const allyFocusables = queryFocusables(allyNode)
        if (allyFocusables.length === 0) return

        // Splice ally focusables immediately after the anchor's position in the
        // trap so portaled dropdowns preserve natural tab order. If there is no
        // anchor (or it isn't in the trap), append at the end as a fallback.
        let insertAt = -1
        if (anchor) {
          for (let i = focusables.length - 1; i >= 0; i -= 1) {
            const el = focusables[i]
            if (el === anchor || anchor.contains(el)) {
              insertAt = i + 1
              break
            }
          }
        }

        if (insertAt === -1) {
          focusables.push(...allyFocusables)
        } else {
          focusables.splice(insertAt, 0, ...allyFocusables)
        }
      })

      return focusables
    }

    const focusableElements = getFocusableElements()
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    }

    const handleTabKey = (event) => {
      if (event.key !== 'Tab') return

      const focusables = getFocusableElements()
      if (focusables.length === 0) return

      const firstElement = focusables[0]
      const lastElement = focusables[focusables.length - 1]
      const currentElement = document.activeElement

      // Always drive movement manually: portaled allies (e.g. CustomSelect search
      // inputs mounted under document.body) break DOM order, so relying on the
      // browser's default Tab behavior lets focus escape the trap.
      event.preventDefault()

      if (!isInsideTrap(currentElement)) {
        ;(event.shiftKey ? lastElement : firstElement).focus()
        return
      }

      const currentIndex = focusables.indexOf(currentElement)
      if (currentIndex === -1) {
        ;(event.shiftKey ? lastElement : firstElement).focus()
        return
      }

      const nextIndex = event.shiftKey
        ? (currentIndex - 1 + focusables.length) % focusables.length
        : (currentIndex + 1) % focusables.length
      focusables[nextIndex].focus()
    }

    document.addEventListener('keydown', handleTabKey)

    return () => {
      document.removeEventListener('keydown', handleTabKey)

      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }
  }, [isActive])

  return { trapRef, registerAlly }
}

export default useFocusTrap
